/**
 * PUBLIC ITEM DETAIL MODAL
 * 
 * Read-only detail modal for viewing items in public collections.
 * Includes "Copy to My Collection" and "Add to PublicWishlist" functionality.
 * Matches the styling of other detail modals in the app.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Copy, Eye, Check, Loader2, ChevronLeft, ChevronRight, ExternalLink, Mail, Flag } from 'lucide-react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getCurrentUser } from '../firebase/auth';
import type { CollectionItem } from '../types';
import { AppIcons } from '../constants/icons';

interface PublicItemDetailModalProps {
  item: CollectionItem;
  onClose: () => void;
  ownerUsername: string;
  ownerDisplayName: string;
  ownerId: string;
  isOwnProfile: boolean;
  onInquire?: () => void;
  initialPhotoIndex?: number;
  onPhotoChange?: (index: number) => void;
}

export default function PublicItemDetailModal({
  item,
  onClose,
  ownerUsername,
  ownerDisplayName,
  ownerId,
  isOwnProfile,
  onInquire,
  initialPhotoIndex = 0,
  onPhotoChange
}: PublicItemDetailModalProps) {
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [addingToWishlist, setAddingToWishlist] = useState(false);
  const [addedToWishlist, setAddedToWishlist] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(initialPhotoIndex);
  const [error, setError] = useState<string | null>(null);

  // Helper to check if a photo is hidden due to DMCA
  const isPhotoHidden = (photo: any): boolean => {
    if (typeof photo === 'object' && photo.dmcaStatus === 'hidden') {
      return true;
    }
    return false;
  };

  // Helper to get URL from photo (string or object)
  const getPhotoUrl = (photo: any): string | null => {
    if (typeof photo === 'string') return photo;
    if (photo?.url) return photo.url;
    return null;
  };

  // Gather all photos, filtering out DMCA-hidden ones for public view
  const allPhotos: string[] = [];
  if (item.photos?.length) {
    item.photos.forEach(p => {
      if (!isPhotoHidden(p)) {
        const url = getPhotoUrl(p);
        if (url) allPhotos.push(url);
      }
    });
  }
  // For these arrays, also check if they're objects with dmcaStatus
  const filterAndAddPhotos = (photos: any[] | undefined) => {
    if (!photos?.length) return;
    photos.forEach(p => {
      if (!isPhotoHidden(p)) {
        const url = getPhotoUrl(p);
        if (url) allPhotos.push(url);
      }
    });
  };
  filterAndAddPhotos(item.swatchPhotos);
  filterAndAddPhotos(item.bottlePhotos);
  filterAndAddPhotos(item.inUsePhotos);
  filterAndAddPhotos(item.macroPhotos);

  // Get icon for item type
  const getItemIcon = () => {
    switch (item.itemType) {
      case 'polish': return AppIcons.Polish;
      case 'plate': return AppIcons.Plate;
      case 'transfer': return AppIcons.Decal;
      case 'embellishment': return AppIcons.Embellishment;
      case 'tool': return AppIcons.Tool;
      case 'powder': return AppIcons.Powder;
      case 'acrylic': return AppIcons.Acrylic;
      default: return AppIcons.Polish;
    }
  };

  const IconComponent = getItemIcon();

  // Copy item to current user's collection
  const handleCopyToCollection = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      setError('Please log in to copy items to your collection');
      return;
    }

    setCopying(true);
    setError(null);

    try {
      // Create a copy of the item without the id and owner-specific fields
      const { id, isForDestash, destashPrice, destashNotes, destashDate, isDestashed, destashCondition, destashQuantityAvailable, isOpenToSwaps, ...itemData } = item;

      const newItem = {
        ...itemData,
        // Reset certain fields for the new owner
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Keep photos - URLs will still work as they're public
        photos: item.photos || [],
        swatchPhotos: item.swatchPhotos || [],
        bottlePhotos: item.bottlePhotos || [],
        inUsePhotos: item.inUsePhotos || [],
        macroPhotos: item.macroPhotos || [],
        // Add source info
        copiedFrom: {
          userId: ownerId,
          username: ownerUsername,
          itemId: id,
          copiedAt: new Date().toISOString()
        },
        // Reset user-specific fields
        quantity: 1,
        timesUsed: 0,
        isFavorite: false,
        onWishlist: false,
        needsInfo: false,
      };

      await addDoc(collection(db, 'users', currentUser.uid, 'items'), newItem);
      
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Error copying item:', err);
      setError('Failed to copy item. Please try again.');
    } finally {
      setCopying(false);
    }
  };

  // Add item to wishlist (copy with onWishlist flag)
  const handleAddToWishlist = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      setError('Please log in to add items to your wishlist');
      return;
    }

    setAddingToWishlist(true);
    setError(null);

    try {
      const { id, isForDestash, destashPrice, destashNotes, destashDate, isDestashed, destashCondition, destashQuantityAvailable, isOpenToSwaps, ...itemData } = item;

      const newItem = {
        ...itemData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        photos: item.photos || [],
        swatchPhotos: item.swatchPhotos || [],
        bottlePhotos: item.bottlePhotos || [],
        inUsePhotos: item.inUsePhotos || [],
        macroPhotos: item.macroPhotos || [],
        copiedFrom: {
          userId: ownerId,
          username: ownerUsername,
          itemId: id,
          copiedAt: new Date().toISOString()
        },
        quantity: 0, // Don't own it yet
        timesUsed: 0,
        isFavorite: false,
        onWishlist: true, // This is the key difference
        isoPriority: 'iso' as const,
        needsInfo: false,
      };

      await addDoc(collection(db, 'users', currentUser.uid, 'items'), newItem);
      
      setAddedToWishlist(true);
      setTimeout(() => setAddedToWishlist(false), 3000);
    } catch (err) {
      console.error('Error adding to wishlist:', err);
      setError('Failed to add to wishlist. Please try again.');
    } finally {
      setAddingToWishlist(false);
    }
  };

  // Format array fields for display
  const formatArrayField = (arr: any): string[] => {
    if (!arr) return [];
    if (typeof arr === 'string') return [arr];
    if (Array.isArray(arr)) return arr;
    return [];
  };

  const currentUser = getCurrentUser();
  const isLoggedIn = !!currentUser;

  // Helper to change photo and notify parent
  const handlePhotoChange = (newIndex: number) => {
    setCurrentPhotoIndex(newIndex);
    onPhotoChange?.(newIndex);
  };

  // Get current photo URL for DMCA report
  const currentPhotoUrl = allPhotos[currentPhotoIndex] || '';

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]"
      onClick={onClose}
    >
      <div
        className="glass-modal w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 bg-slate-800/90 backdrop-blur-md border-b border-slate-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-500/20 rounded-lg">
              <IconComponent className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-glass font-heading">{item.name}</h2>
              <p className="text-sm text-glass-secondary">{item.brand}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700/60 rounded-full transition-default text-glass-secondary hover:text-glass"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left column - Photos */}
              <div>
                {allPhotos.length > 0 ? (
                  <div className="space-y-3">
                    {/* Main photo */}
                    <div className="relative aspect-square bg-slate-800 rounded-xl overflow-hidden">
                      <img
                        src={allPhotos[currentPhotoIndex]}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                      {allPhotos.length > 1 && (
                        <>
                          <button
                            onClick={() => handlePhotoChange(currentPhotoIndex === 0 ? allPhotos.length - 1 : currentPhotoIndex - 1)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-default"
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handlePhotoChange(currentPhotoIndex === allPhotos.length - 1 ? 0 : currentPhotoIndex + 1)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-default"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2 py-1 bg-black/50 rounded-full text-white text-xs">
                            {currentPhotoIndex + 1} / {allPhotos.length}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Thumbnail strip */}
                    {allPhotos.length > 1 && (
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {allPhotos.map((photo, idx) => (
                          <button
                            key={idx}
                            onClick={() => handlePhotoChange(idx)}
                            className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-default ${
                              idx === currentPhotoIndex ? 'border-indigo-500' : 'border-transparent opacity-60 hover:opacity-100'
                            }`}
                          >
                            <img src={photo} alt="" className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Report photo link */}
                    {!isOwnProfile && currentPhotoUrl && (
                      <div className="text-center">
                        <Link
                          to={`/dmcaClaim?photoUrl=${encodeURIComponent(currentPhotoUrl)}&pageUrl=${encodeURIComponent(window.location.href)}`}
                          className="inline-flex items-center gap-1.5 text-xs text-glass-muted hover:text-glass-secondary transition-default"
                        >
                          <Flag className="w-3 h-3" />
                          Report this photo
                        </Link>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="aspect-square bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-xl flex items-center justify-center">
                    <IconComponent className="w-24 h-24 text-glass-secondary" />
                  </div>
                )}
              </div>

              {/* Right column - Details */}
              <div className="space-y-4">
                {/* Owner info */}
                <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                  <span className="text-glass-secondary text-sm">From</span>
                  <a
                    href={`/profile/${ownerUsername}`}
                    className="text-indigo-400 hover:text-indigo-300 font-medium flex items-center gap-1"
                  >
                    {ownerDisplayName}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                {/* Action buttons - only show if not own profile */}
                {!isOwnProfile && (
                  <div className="space-y-2">
                    <button
                      onClick={handleCopyToCollection}
                      disabled={copying || copied || !isLoggedIn}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-default ${
                        copied
                          ? 'bg-green-500 text-white'
                          : 'bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed'
                      }`}
                    >
                      {copying ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : copied ? (
                        <>
                          <Check className="w-5 h-5" />
                          Added to Collection!
                        </>
                      ) : (
                        <>
                          <Copy className="w-5 h-5" />
                          {isLoggedIn ? 'Copy to My Collection' : 'Log in to Copy'}
                        </>
                      )}
                    </button>

                    <button
                      onClick={handleAddToWishlist}
                      disabled={addingToWishlist || addedToWishlist || !isLoggedIn}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-default ${
                        addedToWishlist
                          ? 'bg-purple-500 text-white'
                          : 'bg-purple-600/80 hover:bg-purple-600 text-white disabled:opacity-50 disabled:cursor-not-allowed'
                      }`}
                    >
                      {addingToWishlist ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : addedToWishlist ? (
                        <>
                          <Check className="w-5 h-5" />
                          Added to Wishlist!
                        </>
                      ) : (
                        <>
                          <Eye className="w-5 h-5" />
                          {isLoggedIn ? 'Add to My PublicWishlist' : 'Log in to PublicWishlist'}
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Color description */}
                {item.colorDescription && (
                  <div>
                    <h4 className="text-sm text-glass-secondary mb-1">Description</h4>
                    <p className="text-glass">{item.colorDescription}</p>
                  </div>
                )}

                {/* Polish-specific fields */}
                {item.itemType === 'polish' && (
                  <>
                    {/* Finish */}
                    {formatArrayField(item.finish).length > 0 && (
                      <div>
                        <h4 className="text-sm text-glass-secondary mb-2">Finish</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {formatArrayField(item.finish).map((f, i) => (
                            <span key={i} className="px-2 py-1 bg-slate-600/50 text-glass rounded-lg text-sm">{f}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Color Shift */}
                    {formatArrayField((item as any).colorShift).length > 0 && (
                      <div>
                        <h4 className="text-sm text-glass-secondary mb-2">Color Shift</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {formatArrayField((item as any).colorShift).map((c, i) => (
                            <span key={i} className="px-2 py-1 bg-purple-500/30 text-purple-300 rounded-lg text-sm">{c}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Holo Type */}
                    {formatArrayField((item as any).holoType).length > 0 && (
                      <div>
                        <h4 className="text-sm text-glass-secondary mb-2">Holo Type</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {formatArrayField((item as any).holoType).map((h, i) => (
                            <span key={i} className="px-2 py-1 bg-cyan-500/30 text-cyan-300 rounded-lg text-sm">{h}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Particles */}
                    {formatArrayField((item as any).particles).length > 0 && (
                      <div>
                        <h4 className="text-sm text-glass-secondary mb-2">Particles</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {formatArrayField((item as any).particles).map((p, i) => (
                            <span key={i} className="px-2 py-1 bg-pink-500/30 text-pink-300 rounded-lg text-sm">{p}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Color Family */}
                    {formatArrayField((item as any).colorFamily).length > 0 && (
                      <div>
                        <h4 className="text-sm text-glass-secondary mb-2">Color Family</h4>
                        <div className="flex flex-wrap gap-1.5">
                          {formatArrayField((item as any).colorFamily).map((c, i) => (
                            <span key={i} className="px-2 py-1 bg-slate-600/50 text-glass rounded-lg text-sm">{c}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Tags */}
                {item.tags && item.tags.length > 0 && (
                  <div>
                    <h4 className="text-sm text-glass-secondary mb-2">Tags</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {item.tags.map((tag, i) => (
                        <span key={i} className="px-2 py-1 bg-slate-700 text-glass-secondary rounded-lg text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Collection info */}
                {(item.collectionName || item.releaseYear || item.releaseSeason) && (
                  <div className="grid grid-cols-2 gap-3">
                    {item.collectionName && (
                      <div>
                        <h4 className="text-sm text-glass-secondary">Collection</h4>
                        <p className="text-glass font-medium">{item.collectionName}</p>
                      </div>
                    )}
                    {(item.releaseYear || item.releaseSeason) && (
                      <div>
                        <h4 className="text-sm text-glass-secondary">Release</h4>
                        <p className="text-glass font-medium">
                          {[item.releaseSeason, item.releaseYear].filter(Boolean).join(' ')}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Destash info */}
                {item.isForDestash && (
                  <div className="bg-green-500/20 rounded-xl p-4 border border-green-500/30">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-glass">For Sale / Trade</span>
                      {item.destashPrice && (
                        <span className="text-2xl font-bold text-green-400">
                          ${item.destashPrice}
                        </span>
                      )}
                    </div>
                    {item.destashCondition && (
                      <p className="text-glass-secondary text-sm mb-1">
                        Condition: <span className="text-glass">{item.destashCondition}</span>
                      </p>
                    )}
                    {item.destashNotes && (
                      <p className="text-glass-secondary text-sm mb-3">{item.destashNotes}</p>
                    )}
                    {item.isOpenToSwaps && (
                      <span className="inline-block px-2 py-1 bg-blue-500/30 text-blue-300 rounded-lg text-xs mb-3">
                        Open to Swaps
                      </span>
                    )}
                    {!isOwnProfile && onInquire && (
                      <button
                        onClick={onInquire}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600/80 hover:bg-green-600 text-white rounded-lg transition-default font-medium"
                      >
                        <Mail className="w-4 h-4" />
                        Inquire About This Item
                      </button>
                    )}
                  </div>
                )}

                {/* Additional details grid */}
                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-slate-600">
                  <div>
                    <h4 className="text-sm text-glass-secondary">Type</h4>
                    <p className="text-glass font-medium capitalize">{item.itemType}</p>
                  </div>
                  {item.status && (
                    <div>
                      <h4 className="text-sm text-glass-secondary">Status</h4>
                      <p className="text-glass font-medium capitalize">{item.status}</p>
                    </div>
                  )}
                  {(item as any).polishType && (
                    <div>
                      <h4 className="text-sm text-glass-secondary">Polish Type</h4>
                      <p className="text-glass font-medium capitalize">{(item as any).polishType}</p>
                    </div>
                  )}
                  {(item as any).capacity && (
                    <div>
                      <h4 className="text-sm text-glass-secondary">Size</h4>
                      <p className="text-glass font-medium">{(item as any).capacity}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 bg-slate-800/90 backdrop-blur-md border-t border-slate-600 px-6 py-4">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-glass rounded-lg transition-default font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
